import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { payrollPayments, type PayrollPaymentListItem } from '@/api/payrolls';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const METHOD_LABEL: Record<string, string> = {
  CASH: 'Готівка (РКО)',
  BANK: 'Банк',
};
const KIND_LABEL: Record<string, string> = {
  MAIN: 'ЗП основна', SALARY: 'ЗП основна', ADVANCE: 'Аванс', VACATION: 'Відпустка',
  COMPENSATION: 'Компенсація відпустки',
  SICK_ORG: 'Лікарняний (орг)', SICK_PFU: 'Лікарняний (ПФУ)', SICK: 'Лікарняні',
  CIVIL: 'Виплата за ЦПУ',
};

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

export default function PayrollPaymentsPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [orgId, setOrgId] = useState('');
  const [kind, setKind] = useState('');
  const [periodYear, setPeriodYear] = useState<number | ''>(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState<number | ''>(now.getMonth() + 1);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const list = useQuery({
    queryKey: ['payroll-payments', orgId, kind, periodYear, periodMonth],
    queryFn: () => payrollPayments.list({
      limit: 200,
      organization_id: orgId || undefined,
      payout_kind: kind || undefined,
      period_year: periodYear || undefined,
      period_month: periodMonth || undefined,
    }),
  });

  const cols = useMemo<ColumnDef<PayrollPaymentListItem>[]>(() => [
    { key: 'document_number', header: '№', width: 140, importBadge: true, sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'organization', header: 'Організація', width: 170,
      sortValue: (r) => orgName.get(r.organization_id) ?? '',
      cell: (r) => orgName.get(r.organization_id) ?? '—',
    },
    {
      key: 'document_date', header: 'Дата', width: 150,
      sortValue: (r) => r.document_date ?? '',
      cell: (r) => r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : '',
    },
    {
      key: 'period', header: 'Період', width: 130,
      sortValue: (r) => (r.period_year ?? 0) * 100 + (r.period_month ?? 0),
      cell: (r) => r.period_month ? `${MONTHS[r.period_month]} ${r.period_year}` : '—',
    },
    {
      key: 'payout_kind', header: 'Вид виплати', width: 130,
      sortValue: (r) => r.payout_kind ?? '',
      cell: (r) => KIND_LABEL[r.payout_kind ?? 'MAIN'] ?? (r.payout_kind ?? '—'),
    },
    {
      key: 'payment_method', header: 'Метод', width: 140,
      sortValue: (r) => METHOD_LABEL[r.payment_method] ?? (r.payment_method ?? ''),
      cell: (r) => METHOD_LABEL[r.payment_method] ?? r.payment_method,
    },
    {
      key: 'total_amount', header: 'Сума', width: 150, align: 'right',
      sortValue: (r) => Number(r.total_amount),
      cell: (r) => fmt2(r.total_amount),
    },
    {
      key: 'posted', header: 'Проведено', width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0,
      cell: (r) => r.posted ? '✓ так' : 'ні',
    },
  ], [orgName]);

  const hasFilters = orgId || kind || periodMonth !== '' || periodYear !== '';

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Виплата нарахувань</h2>
        <button className="primary" onClick={() => navigate('/payroll-payments/new')}>
          + Нова виплата
        </button>
      </div>
      {/* Фільтри — окремим рядком під заголовком (вимога власника 2026-07-06). */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end',
                    flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 180, margin: 0 }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— усі —</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ minWidth: 150, margin: 0 }}>
          <label>Вид виплати</label>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">— усі —</option>
            <option value="MAIN">ЗП основна</option>
            <option value="ADVANCE">Аванс</option>
            <option value="VACATION">Відпустка</option>
            <option value="COMPENSATION">Компенсація відпустки</option>
            <option value="SICK_ORG">Лікарняний (орг)</option>
            <option value="SICK_PFU">Лікарняний (ПФУ)</option>
            <option value="CIVIL">Виплата за ЦПУ</option>
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={periodMonth}
              onChange={(e) => setPeriodMonth(e.target.value ? Number(e.target.value) : '')}
              style={{ flex: 2 }}>
              <option value="">— усі —</option>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={periodYear}
              onChange={(e) => setPeriodYear(e.target.value ? Number(e.target.value) : '')}
              style={{ flex: 1 }}>
              <option value="">— усі —</option>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
        {hasFilters && (
          <button onClick={() => { setOrgId(''); setKind(''); setPeriodMonth(''); setPeriodYear(''); }}>
            Скинути
          </button>
        )}
      </div>
      <DataTable<PayrollPaymentListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає документів."
        onRowDoubleClick={(r) => navigate(`/payroll-payments/${r.id}`)}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </div>
  );
}
